import { api } from '@goaltree/backend/convex/_generated/api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import {
  CalendarPlusIcon,
  CheckSquareIcon,
  CompassIcon,
  FlameIcon,
  LayersIcon,
  PencilLineIcon,
  TargetIcon,
} from 'lucide-react'
import {
  type ComponentProps,
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useGoals } from '@/hooks/use-goals'
import { NEW_EVENT_SHORTCUT_LABEL, NEW_GOAL_SHORTCUT_LABEL } from '@/lib/capture'

export function useOpenNewGoal() {
  const navigate = useNavigate()
  return useCallback(() => {
    sessionStorage.setItem('newgoal_from', window.location.pathname)
    void navigate({ to: '/goals/new', search: { goal: undefined } })
  }, [navigate])
}

export function openNewPost() {
  window.dispatchEvent(new CustomEvent('open-new-post'))
}

export type QuickAddKind = 'tasks' | 'habits' | 'event'

// Opens the Everyday composer (in place when already there) for a task, habit or event.
export function useQuickAdd() {
  const navigate = useNavigate()
  const goals = useGoals()
  const ensureInbox = useMutation(api.nodes.ensureInbox)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const hasInbox = !!goals?.some((g) => g.isInbox)
  return useCallback(
    async (kind: QuickAddKind) => {
      const event = kind === 'event'
      const tab = kind === 'habits' ? 'habits' : event ? 'events' : 'tasks'
      if (pathname.startsWith('/everyday')) {
        window.dispatchEvent(new CustomEvent('inbox-compose', { detail: { tab, event } }))
        return
      }
      if (!hasInbox) await ensureInbox()
      void navigate({
        to: '/everyday',
        search: { addTask: true, tab, ...(event && { compose: 'event' as const }) },
      })
    },
    [pathname, hasInbox, ensureInbox, navigate],
  )
}

export function NewMenu({
  trigger,
  children,
  onPick,
  showShortcut = true,
  side = 'bottom',
  align = 'start',
}: {
  trigger: ReactElement
  children: ReactNode
  onPick?: () => void
  showShortcut?: boolean
  side?: ComponentProps<typeof DropdownMenuContent>['side']
  align?: ComponentProps<typeof DropdownMenuContent>['align']
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const openNewGoal = useOpenNewGoal()
  const quickAdd = useQuickAdd()
  type Item = {
    key: string
    icon: ReactNode
    label: string
    hint: string
    shortcut?: string
    run: () => void
  }
  const groups: Item[][] = [
    [
      {
        key: 'task',
        icon: <CheckSquareIcon />,
        label: t('nav.newMenuTask'),
        hint: t('nav.newMenuTaskHint'),
        shortcut: 'T',
        run: () => void quickAdd('tasks'),
      },
      {
        key: 'event',
        icon: <CalendarPlusIcon />,
        label: t('nav.newMenuEvent'),
        hint: t('nav.newMenuEventHint'),
        shortcut: NEW_EVENT_SHORTCUT_LABEL,
        run: () => void quickAdd('event'),
      },
      {
        key: 'habit',
        icon: <FlameIcon />,
        label: t('nav.newMenuHabit'),
        hint: t('nav.newMenuHabitHint'),
        shortcut: 'H',
        run: () => void quickAdd('habits'),
      },
      {
        key: 'blueprint',
        icon: <CompassIcon />,
        label: t('templates.bp.menu'),
        hint: t('templates.bp.menuHint'),
        run: () =>
          void navigate({ to: '/explore', search: { tab: 'templates', blueprints: true } }),
      },
    ],
    [
      {
        key: 'goal',
        icon: <TargetIcon />,
        label: t('nav.newMenuGoal'),
        hint: t('nav.newMenuGoalHint'),
        shortcut: NEW_GOAL_SHORTCUT_LABEL,
        run: openNewGoal,
      },
      {
        key: 'template',
        icon: <LayersIcon />,
        label: t('nav.newMenuTemplate'),
        hint: t('nav.newMenuTemplateHint'),
        run: () => void navigate({ to: '/explore', search: { tab: 'templates' } }),
      },
    ],
    [
      {
        key: 'post',
        icon: <PencilLineIcon />,
        label: t('nav.newMenuPost'),
        hint: t('nav.newMenuPostHint'),
        run: openNewPost,
      },
    ],
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={trigger}>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 p-1" side={side} align={align}>
        {groups.map((items, i) => (
          <Fragment key={items[0].key}>
            {i > 0 && <DropdownMenuSeparator />}
            {items.map(({ key, icon, label, hint, shortcut, run }) => (
              <DropdownMenuItem
                key={key}
                className="cursor-pointer items-start gap-2.5 px-2.5 py-2 [&_svg]:mt-0.5 [&_svg]:text-muted-foreground"
                onClick={() => {
                  onPick?.()
                  run()
                }}
              >
                {icon}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-medium">{label}</span>
                  <span className="truncate text-xs text-muted-foreground">{hint}</span>
                </span>
                {showShortcut && shortcut && (
                  <DropdownMenuShortcut className="mt-0.5 font-mono">
                    {shortcut}
                  </DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
